import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Download,
  Filter,
  AlertTriangle,
  Check,
  TrendingUp,
  TrendingDown,
  BarChart4,
  X,
  Loader2,
  Package,
  Users,
  Info
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { InventoryAPI, InventoryProduct, InventorySupplierDetailedResponse, InventoryDashboard } from '../lib/api';
import { toast } from '../components/ui/use-toast';

// Supplier Detail Component Props
interface SupplierDetailModalProps {
  supplierId: string;
  onClose: () => void;
}

// Supplier Detail Component
const SupplierDetailModal = ({ supplierId, onClose }: SupplierDetailModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [supplierData, setSupplierData] = useState<InventorySupplierDetailedResponse | null>(null);

  useEffect(() => {
    const fetchSupplierData = async () => {
      try {
        setIsLoading(true);
        const response = await InventoryAPI.getSupplierInventory(supplierId);
        if (response.success) {
          setSupplierData(response.data);
        }
      } catch (error) {
        console.error("Error fetching supplier details:", error);
        toast({
          title: "Error",
          description: "Failed to load supplier details",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (supplierId) {
      fetchSupplierData();
    }
  }, [supplierId]);

  if (!supplierId) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-background rounded-t-lg sm:rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-10 sm:slide-in-from-bottom-0">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {isLoading ? "Loading Supplier Details..." : supplierData ? `${supplierData.supplier.name} Inventory` : "Supplier Details"}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <Loader2 className="h-8 w-8 animate-spin mr-2" />
              <span>Loading supplier details...</span>
            </div>
          ) : supplierData ? (
            <div className="space-y-6">
              {/* Supplier Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <h4 className="text-sm font-medium mb-2">Supplier Information</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">Email:</span> {supplierData.supplier.email}</p>
                      <p><span className="text-muted-foreground">Phone:</span> {supplierData.supplier.phone}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <h4 className="text-sm font-medium mb-2">Inventory Statistics</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total Products:</p>
                        <p className="font-medium">{supplierData.inventoryStats.totalProducts}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Stock:</p>
                        <p className="font-medium">{supplierData.inventoryStats.totalStock}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Low Stock Items:</p>
                        <p className="font-medium">{supplierData.inventoryStats.lowStockCount}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Out of Stock:</p>
                        <p className="font-medium">{supplierData.inventoryStats.outOfStockCount}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Products Table */}
              <div>
                <h4 className="text-sm font-medium mb-2">Supplier Products</h4>
                <div className="border rounded-md overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Current Stock</TableHead>
                        <TableHead>Reserved</TableHead>
                        <TableHead>Available</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supplierData.products.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                            No products found for this supplier
                          </TableCell>
                        </TableRow>
                      ) : (
                        supplierData.products.map((item) => {
                          let stockStatus;
                          if (item.inventory.isOutOfStock) {
                            stockStatus = { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
                          } else if (item.inventory.isLowStock) {
                            stockStatus = { label: 'Low', color: 'bg-yellow-100 text-yellow-800' };
                          } else {
                            stockStatus = { label: 'Optimal', color: 'bg-green-100 text-green-800' };
                          }

                          return (
                            <TableRow key={item.product._id}>
                              <TableCell>
                                <div className="font-medium">{item.product.name}</div>
                                <div className="text-xs text-muted-foreground">{item.product.sku}</div>
                              </TableCell>
                              <TableCell>{item.product.category.name}</TableCell>
                              <TableCell>{item.inventory.currentStock}</TableCell>
                              <TableCell>{item.inventory.reservedStock}</TableCell>
                              <TableCell>{item.inventory.availableStock}</TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                                  {stockStatus.label}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Recent Transactions */}
              <div>
                <h4 className="text-sm font-medium mb-2">Recent Transactions</h4>
                <div className="border rounded-md overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Transaction</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Updated By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!supplierData.recentTransactions || supplierData.recentTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                            No recent transactions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        supplierData.recentTransactions.map((transaction) => (
                          <TableRow key={transaction._id}>
                            <TableCell>{transaction.product?.name || 'Unknown Product'}</TableCell>
                            <TableCell>
                              <span className={`capitalize ${transaction.type === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                                {transaction.type}
                              </span>
                            </TableCell>
                            <TableCell>{transaction.quantity}</TableCell>
                            <TableCell>{new Date(transaction.createdAt).toLocaleDateString()}</TableCell>
                            <TableCell>{transaction.performedBy?.name || 'Unknown User'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              No supplier data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface SelectedProduct {
  id: string;
  product: string;
  currentStock: number;
  unit: string;
}

interface Adjustment {
  quantity: string;
  reason: string;
}

export default function InventoryPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isAdjustStockModalOpen, setIsAdjustStockModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct>({
    id: '',
    product: '',
    currentStock: 0,
    unit: ''
  });
  const [adjustment, setAdjustment] = useState<Adjustment>({
    quantity: '',
    reason: 'restock'
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 1
  });
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<InventoryDashboard | null>(null);
  const [products, setProducts] = useState<InventoryProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      const response = await InventoryAPI.getDashboard();
      if (response.success) {
        setDashboardData(response.data);

        // Extract unique categories from inventory by category
        const uniqueCategories = response.data.inventoryByCategory.map(cat => cat.name);
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast({
        title: "Error",
        description: "Failed to load inventory dashboard",
        variant: "destructive"
      });
    }
  };

  // Fetch product inventory
  const fetchInventory = async (page = 1) => {
    setIsLoading(true);
    try {
      // Build API params based on selected filters
      const params: any = {
        page,
        limit: pagination.limit,
        search: searchQuery,
      };

      if (selectedCategory) {
        params.category = selectedCategory;
      }

      if (selectedStatus === 'low') {
        params.lowStock = true;
      } else if (selectedStatus === 'critical') {
        params.outOfStock = true;
      }

      const response = await InventoryAPI.getAllProducts(params);
      if (response.success) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toast({
        title: "Error",
        description: "Failed to load inventory data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchInventory(1); // Reset to first page when filters change
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCategory, selectedStatus]);

  const handleAdjustStock = () => {
    // This would call the update stock API in a real implementation
    toast({
      title: "Feature Not Implemented",
      description: "Stock adjustment is not implemented as we're focusing on display-only functionality.",
    });
    setIsAdjustStockModalOpen(false);
    setAdjustment({ quantity: '', reason: 'restock' });
  };

  // Calculate inventory stats for optimal stock
  const getOptimalStockCount = () => {
    if (!dashboardData) return 0;
    return dashboardData.productsWithInventory - dashboardData.lowStockCount - dashboardData.outOfStockCount;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold">Inventory</h1>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className={`cursor-pointer ${selectedStatus === 'all' ? 'border-primary ring-1 ring-primary' : ''}`}
          onClick={() => setSelectedStatus('all')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                {isLoading && !dashboardData ? (
                  <p className="text-2xl font-bold flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading
                  </p>
                ) : (
                  <p className="text-2xl font-bold">
                    {dashboardData?.productsWithInventory || 0}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">Total Products</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Package className="h-4 w-4 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer ${selectedStatus === 'optimal' ? 'border-primary ring-1 ring-primary' : ''}`}
          onClick={() => setSelectedStatus('optimal')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                {isLoading && !dashboardData ? (
                  <p className="text-2xl font-bold flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading
                  </p>
                ) : (
                  <p className="text-2xl font-bold">
                    {getOptimalStockCount()}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">Optimal Stock</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer ${selectedStatus === 'low' ? 'border-primary ring-1 ring-primary' : ''}`}
          onClick={() => setSelectedStatus('low')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                {isLoading && !dashboardData ? (
                  <p className="text-2xl font-bold flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading
                  </p>
                ) : (
                  <p className="text-2xl font-bold">{dashboardData?.lowStockCount || 0}</p>
                )}
                <p className="text-sm text-muted-foreground">Low Stock</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer ${selectedStatus === 'critical' ? 'border-primary ring-1 ring-primary' : ''}`}
          onClick={() => setSelectedStatus('critical')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                {isLoading && !dashboardData ? (
                  <p className="text-2xl font-bold flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading
                  </p>
                ) : (
                  <p className="text-2xl font-bold">{dashboardData?.outOfStockCount || 0}</p>
                )}
                <p className="text-sm text-muted-foreground">Out of Stock</p>
              </div>
              <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Suppliers */}
      {dashboardData?.topSuppliers && dashboardData.topSuppliers.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Top Suppliers</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {dashboardData.topSuppliers.slice(0, 4).map((supplier) => (
              <Card
                key={supplier._id}
                className="cursor-pointer hover:border-primary hover:shadow-md transition-shadow"
                onClick={() => {
                  if (supplier && supplier._id) {
                    setSelectedSupplierId(supplier._id);
                  } else {
                    toast({
                      title: "Error",
                      description: "Unable to load supplier details.",
                      variant: "destructive"
                    });
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-primary" />
                    <h3 className="font-medium">{supplier?.name || 'Unknown Supplier'}</h3>
                  </div>
                  <div className="mt-2 grid grid-cols-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Products</p>
                      <p className="font-medium">{supplier?.productsCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Stock</p>
                      <p className="font-medium">{supplier?.totalStock || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Search and filter */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products or SKU..."
            className="pl-8 w-full sm:w-[280px]"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <select
            className="h-9 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" className="h-9">
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Inventory table */}
      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="hidden md:table-cell">SKU</TableHead>
                <TableHead className="hidden lg:table-cell">Category</TableHead>
                <TableHead>Current Stock</TableHead>
                <TableHead className="hidden md:table-cell">Available</TableHead>
                <TableHead className="hidden lg:table-cell">Suppliers</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10">
                    <div className="flex justify-center items-center">
                      <Loader2 className="h-6 w-6 mr-2 animate-spin" />
                      <span>Loading inventory data...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                    No inventory data found. Try adjusting your search or filters.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((item) => {
                  // Determine stock status
                  let stockStatus;
                  if (item.isOutOfStock) {
                    stockStatus = { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
                  } else if (item.isLowStock) {
                    stockStatus = { label: 'Low', color: 'bg-yellow-100 text-yellow-800' };
                  } else {
                    stockStatus = { label: 'Optimal', color: 'bg-green-100 text-green-800' };
                  }

                  return (
                    <TableRow key={item._id}>
                      <TableCell className="font-medium">
                        <div>{item.name}</div>
                        <div className="text-xs text-muted-foreground md:hidden">
                          {item.sku}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {item.sku}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {item.category.name}
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">{item.totalStock.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground md:hidden">
                          Available: {item.totalAvailable.toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {item.totalAvailable.toLocaleString()}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {item.suppliersCount > 0 ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs"
                            onClick={() => {
                              if (item.inventoryDetails && item.inventoryDetails.length > 0 && item.inventoryDetails[0].supplierId) {
                                setSelectedSupplierId(item.inventoryDetails[0].supplierId);
                              } else {
                                toast({
                                  title: "No Supplier Details",
                                  description: "This product has no supplier details available.",
                                  variant: "destructive"
                                });
                              }
                            }}
                          >
                            {item.suppliersCount} suppliers
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">No suppliers</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                          {stockStatus.label}
                        </span>
                      </TableCell>

                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          Showing <strong>{products.length ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)}</strong> of <strong>{pagination.total}</strong> products
        </div>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page <= 1 || isLoading}
            onClick={() => fetchInventory(pagination.page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.pages || isLoading}
            onClick={() => fetchInventory(pagination.page + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Adjust Stock Modal */}
      {isAdjustStockModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-background rounded-t-lg sm:rounded-lg shadow-lg w-full max-w-md animate-in slide-in-from-bottom-10 sm:slide-in-from-bottom-0">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {selectedProduct.id ? 'Adjust Stock Level' : 'Add Stock Adjustment'}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdjustStockModalOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              {selectedProduct.id ? (
                <div>
                  <p className="text-sm font-medium mb-1">Product</p>
                  <p>{selectedProduct.product}</p>
                  <div className="mt-2 flex justify-between text-sm">
                    <span>Current Stock: <strong>{selectedProduct.currentStock} {selectedProduct.unit}</strong></span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="product">
                    Product
                  </label>
                  <select
                    id="product"
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    onChange={(e) => {
                      const product = products.find(p => p._id === e.target.value);
                      if (product && product.inventoryDetails.length > 0) {
                        setSelectedProduct({
                          id: product._id,
                          product: product.name,
                          currentStock: product.inventoryDetails[0].currentStock,
                          unit: 'unit'
                        });
                      }
                    }}
                  >
                    <option value="">Select Product</option>
                    {products.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="adjustmentType">
                  Adjustment Type
                </label>
                <select
                  id="adjustmentType"
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={adjustment.reason}
                  onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                >
                  <option value="restock">Restock (Increase)</option>
                  <option value="sale">Sale (Decrease)</option>
                  <option value="return">Return (Increase)</option>
                  <option value="damage">Damage (Decrease)</option>
                  <option value="correction">Inventory Correction</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="quantity">
                  Quantity {adjustment.reason === 'correction' ? '' :
                    (adjustment.reason === 'restock' || adjustment.reason === 'return') ? '(Add)' : '(Remove)'}
                </label>
                <Input
                  id="quantity"
                  type="number"
                  placeholder="Enter quantity"
                  value={adjustment.quantity}
                  onChange={(e) => setAdjustment({ ...adjustment, quantity: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setIsAdjustStockModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAdjustStock}>
                {selectedProduct.id ? 'Adjust Stock' : 'Add Adjustment'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Detail Modal */}
      {selectedSupplierId && (
        <SupplierDetailModal
          supplierId={selectedSupplierId}
          onClose={() => setSelectedSupplierId(null)}
        />
      )}
    </div>
  );
} 